// LTP market-data socket client — Path A compatibility shim.
//
// Speaks the NEW binary protocol of wss://wsn.kalpvruks.com (typed frames:
// basic/full/OI/index, little-endian) but RE-EMITS the OLD `|`-delimited
// PubSub payloads on the same topics, so existing widgets (LtpData,
// IndicesWidget, …) keep working unchanged.
//
// Notes / current limitations of the shim:
//  - Compression is NOT negotiated, so the server sends uncompressed frames
//    (no LZ4 dependency needed in the browser).
//  - Greeks are skipped for now (the new feed has no greeks message).
//  - Currency (NSECD) has no segment in the new feed, so it is unsupported.
//  - Depth blob (mbpInfos) and buy/sell colours don't exist in the binary
//    frames, so those payload slots are left blank; rendered price/volume/
//    change and 5-level bid/ask level-0 are populated.
import PubSub from 'pubsub-js'

// New binary feed endpoint (see reference LTPConnector).
const LTP_SOCKET_URL = 'wss://wsn.kalpvruks.com:443'

// ---- connection + subscription state ----
let accessToken = null
let reconnectTimer = null
let reconnectCount = 0
let manualClose = false
let warnedCompressed = false
// key -> subscription JSON; replayed on every (re)connect
const subscriptions = new Map()

const BASE_DELAY_MS = 1500
const MAX_DELAY_MS = 15000

// message type + segment constants (mirror server enum)
const MSG_BASIC = 0x02
const MSG_FULL = 0x04
const MSG_OI = 0x05
const MSG_INDEX = 0x0a
const MAGIC_COMPRESSED = 0xc0
const SEG_BSEFO = 3
const SEG_BSECM = 4

const isOpen = () => typeof WebSocket !== 'undefined' && global.ws && global.ws.readyState === WebSocket.OPEN

const rawSend = (payload) => {
    if (!isOpen()) return false
    global.ws.send(JSON.stringify(payload))
    return true
}

// exch shorthand (as passed by the widgets) -> new segment code
const mapExchToSeg = (exch) => {
    switch (String(exch || '').toUpperCase()) {
        case 'EQ':
        case 'CM':
        case 'NSECM': return 'NSECM'
        case 'FNO':
        case 'FO':
        case 'NSEFO': return 'NSEFO'
        case 'BSE':
        case 'BSECM': return 'BSECM'
        case 'BSEFNO':
        case 'BSEFO': return 'BSEFO'
        default: return '' // currency / unknown — not supported by the new feed
    }
}

const track = (key, payload) => {
    subscriptions.set(key, payload)
    rawSend(payload)
}
const untrack = (key) => {
    subscriptions.delete(key)
    // The new protocol has no documented per-token unsubscribe; drop it locally
    // so it isn't replayed on reconnect (server stops on next reconnect).
}

// ---- public subscription API (unchanged signatures) ----
export const join = (token, exch) => {
    const seg = mapExchToSeg(exch)
    if (!seg) return
    track(`b:${seg}:${token}`, { join: String(token), seg, type: 'basic' })
}
export const remove = (token, exch) => {
    const seg = mapExchToSeg(exch)
    if (seg) untrack(`b:${seg}:${token}`)
}

export const joinBest5 = (token, exch) => {
    const seg = mapExchToSeg(exch)
    if (!seg) return
    // full subscription -> depth frames (also enriches the main payload)
    track(`f:${seg}:${token}`, { join: String(token), seg, type: 'full' })
}
export const removeBest5 = (token, exch) => {
    const seg = mapExchToSeg(exch)
    if (seg) untrack(`f:${seg}:${token}`)
}

export const joinLTP = (token, exch) => join(token, exch)
export const removeLTP = (token, exch) => remove(token, exch)
export const joinLTPChange = (token, exch) => join(token, exch)
export const removeLTPChange = (token, exch) => remove(token, exch)

export const joinOI = (token, exch) => {
    const seg = mapExchToSeg(exch)
    if (!seg) return
    track(`o:${seg}:${token}`, { joinOI: String(token), seg })
}
export const removeOI = (token, exch) => {
    const seg = mapExchToSeg(exch)
    if (seg) untrack(`o:${seg}:${token}`)
}

// Greeks skipped for now (no greeks message in the new feed). Kept as no-ops
// so existing imports don't break.
export const joinGreeks = () => {}
export const removeGreeks = () => {}

// ---- reconnect ----
const clearReconnect = () => {
    if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null }
}
const scheduleReconnect = () => {
    if (manualClose) return
    clearReconnect()
    const delay = Math.min(MAX_DELAY_MS, Math.round(BASE_DELAY_MS * Math.pow(1.5, reconnectCount)))
    reconnectCount += 1
    reconnectTimer = setTimeout(() => { reconnectTimer = null; openSocket() }, delay)
}

// ---- little-endian helpers over a DataView ----
const ltt2str = (secs) => {
    if (!secs || secs <= 0) return ''
    try { return new Date(secs * 1000).toLocaleTimeString('en-GB') } catch (_) { return '' }
}

const INDEX_NAMES = ['Nifty Fin Service', 'NIFTY MID SELECT', 'Nifty Bank', 'Nifty 50', 'India VIX', 'SENSEX', 'BANKEX']
// Matching used to fall back to `up.includes(W)`, which is too loose for this
// name set: 'NIFTY 500' contains 'NIFTY 50' and 'SENSEX 50' contains 'SENSEX',
// so those broader indices were relabelled as Nifty 50 / SENSEX and published on
// the same topic. The widget then showed whichever of the two ticked last, which
// is why the Nifty 50 row jumped between unrelated levels.
//
// An exact match wins outright; otherwise the name may only differ by an
// exchange prefix ('NSE NIFTY 50'), so the candidate must be a whole trailing
// word. That accepts the prefixed forms and rejects every collision above.
const endsWithWholeName = (up, W) => {
    if (!up.endsWith(W)) return false
    const before = up[up.length - W.length - 1]

    return before === undefined || before === ' '
}

const normalizeIndexName = (raw) => {
    const up = String(raw || '').trim().toUpperCase()
    if (!up) return ''

    for (const w of INDEX_NAMES) {
        if (up === w.toUpperCase()) return w
    }

    for (const w of INDEX_NAMES) {
        if (endsWithWholeName(up, w.toUpperCase())) return w
    }

    return String(raw || '').trim()
}

// MsgIndex (0x0A) -> 'Indices' topic: name|ltp(paise)|indicator|pct(x100)|close(paise)
const handleIndex = (dv, len) => {
    if (len < 56) return
    let nameLen = 0
    while (nameLen < 21 && dv.getUint8(6 + nameLen) !== 0) nameLen++
    let raw = ''
    for (let i = 0; i < nameLen; i++) raw += String.fromCharCode(dv.getUint8(6 + i))
    const name = normalizeIndexName(raw)
    if (!name) return

    const valueP = dv.getInt32(28, true)
    const changeP = dv.getInt32(48, true) // change in paise (points × 100)
    const pctX100 = dv.getInt16(52, true)
    const indicator = changeP >= 0 ? '+' : '-'

    // Field layout expected by IndicesWidget: name|ltp|indicator|pct|change
    // (the widget's "close" slot actually renders the change amount).
    PubSub.publish('Indices', `${name}|${valueP}|${indicator}|${pctX100}|${changeP}`)
}

// build the main cash/fno payload in the exact field layout LtpData expects
const emitMainPayload = (token, seg, f) => {
    // f: { ltpP, openP, highP, lowP, closeP, volU, ltq, lttStr,
    //      bidQty0, bidPrice0, askQty0, askPrice0, atpP, totBuy, totSell }
    const per = f.closeP > 0 ? (((f.ltpP - f.closeP) / f.closeP) * 100).toFixed(2) : '0'
    const chng = f.ltpP - f.closeP // paise
    const isBSE = seg === SEG_BSEFO || seg === SEG_BSECM

    let arr
    if (isBSE) {
        // myBSESubscriber field layout (indices it actually reads)
        arr = new Array(33).fill('')
        arr[0] = token
        arr[1] = f.openP
        arr[2] = f.closeP        // prev close (change denominator)
        arr[3] = f.highP
        arr[4] = f.lowP
        arr[6] = f.volU
        arr[8] = f.ltq
        arr[9] = f.ltpP
        arr[10] = f.closeP
        arr[14] = f.totBuy
        arr[15] = f.totSell
        arr[24] = f.lttStr
        arr[27] = f.bidPrice0
        arr[28] = f.bidQty0
        arr[31] = f.askPrice0
        arr[32] = f.askQty0
    } else {
        // mySubscriber (NSE) 23-field layout
        arr = new Array(23).fill('')
        arr[0] = f.ltpP
        arr[1] = per
        arr[2] = f.lowP
        arr[3] = f.highP
        arr[4] = f.bidQty0
        arr[5] = f.bidPrice0
        arr[6] = f.askQty0
        arr[7] = f.askPrice0
        arr[8] = f.atpP
        arr[9] = f.volU
        arr[10] = chng
        arr[11] = '' // mbpInfos (depth blob) — not reconstructable from binary
        arr[12] = f.totSell
        arr[13] = f.totBuy
        arr[14] = f.openP
        arr[15] = f.closeP
        arr[16] = f.ltq
        arr[17] = f.lttStr
        arr[18] = token
        // [19..22] buy/sell colours — absent in binary feed
    }
    PubSub.publish(token, arr.join('|'))
    PubSub.publish(`${token}LTP`, `${f.ltpP}|${per}`)
}

// MsgBasic (0x02, 45B)
const handleBasic = (dv, len, seg) => {
    if (len < 45) return
    const token = String(dv.getUint32(6, true))
    const ltpP = dv.getInt32(10, true)
    if (ltpP <= 0) return
    const openP = dv.getInt32(14, true)
    const highP = dv.getInt32(18, true)
    const lowP = dv.getInt32(22, true)
    const closeP = dv.getInt32(26, true)
    const volU = dv.getUint32(36, true)
    const oiU = dv.getUint32(41, true)

    emitMainPayload(token, seg, {
        ltpP, openP, highP, lowP, closeP, volU,
        ltq: '', lttStr: '',
        bidQty0: '', bidPrice0: '', askQty0: '', askPrice0: '',
        atpP: '', totBuy: '', totSell: '',
    })
    if (oiU > 0) PubSub.publish(`${token}OI`, String(oiU))
}

// MsgFull (0x04, 182B — depth)
const handleFull = (dv, len, seg) => {
    if (len < 38) return
    const token = String(dv.getUint32(6, true))
    const ltpP = dv.getInt32(10, true)
    if (ltpP <= 0) return

    if (len < 162) {
        // header-only fallback: price only
        emitMainPayload(token, seg, {
            ltpP, openP: '', highP: '', lowP: '', closeP: dv.getInt32(26, true), volU: '',
            ltq: '', lttStr: '', bidQty0: '', bidPrice0: '', askQty0: '', askPrice0: '',
            atpP: '', totBuy: '', totSell: '',
        })
        return
    }

    const openP = dv.getInt32(14, true)
    const highP = dv.getInt32(18, true)
    const lowP = dv.getInt32(22, true)
    const closeP = dv.getInt32(26, true)
    const atpP = dv.getInt32(30, true)
    const volU = dv.getUint32(34, true)
    const ltq = dv.getInt32(38, true)
    const lttSec = dv.getInt32(42, true)
    const totBuy = Number(dv.getBigInt64(46, true))
    const totSell = Number(dv.getBigInt64(54, true))
    // bid level 0 @62 (price i32, qty i32, orders i16); ask level 0 @112
    const bidPrice0 = dv.getInt32(62, true)
    const bidQty0 = dv.getInt32(66, true)
    const askPrice0 = dv.getInt32(112, true)
    const askQty0 = dv.getInt32(116, true)

    emitMainPayload(token, seg, {
        ltpP, openP, highP, lowP, closeP, volU,
        ltq, lttStr: ltt2str(lttSec),
        bidQty0, bidPrice0, askQty0, askPrice0,
        atpP, totBuy, totSell,
    })
}

// MsgOi (0x05, 18B)
const handleOi = (dv, len) => {
    if (len < 14) return
    const token = String(dv.getUint32(6, true))
    const oiU = dv.getUint32(10, true)
    if (oiU > 0) PubSub.publish(`${token}OI`, String(oiU))
}

const parseFrame = (buf) => {
    if (!buf || buf.byteLength < 2) return
    const dv = new DataView(buf)
    if (dv.getUint8(0) === MAGIC_COMPRESSED) {
        // We never negotiate LZ4, so this shouldn't arrive. If it does, we can't
        // decode without an LZ4 lib — warn once and skip.
        if (!warnedCompressed) {
            warnedCompressed = true
            console.warn('[LTP] Received LZ4-compressed frame but compression was not negotiated — dropping')
        }
        return
    }
    const len = buf.byteLength
    const msgType = dv.getUint8(0)
    const seg = dv.getUint8(1)
    switch (msgType) {
        case MSG_BASIC: handleBasic(dv, len, seg); break
        case MSG_FULL: handleFull(dv, len, seg); break
        case MSG_OI: handleOi(dv, len); break
        case MSG_INDEX: handleIndex(dv, len); break
        default: break
    }
}

const openSocket = () => {
    if (typeof WebSocket === 'undefined' || !accessToken) return

    let ws
    try {
        // access token is passed as the WebSocket subprotocol (matches the new server)
        ws = new WebSocket(LTP_SOCKET_URL, [accessToken])
    } catch (e) {
        console.log('LTP Socket connect error:', e?.message || e)
        scheduleReconnect()
        return
    }

    global.ws = ws
    ws.binaryType = 'arraybuffer'

    ws.onopen = () => {
        if (global.ws !== ws) return
        console.log('LTP Socket Connected')
        reconnectCount = 0
        clearReconnect()
        // Subscribe to index frames for all four segments (server filters).
        for (const seg of ['NSEFO', 'NSECM', 'BSEFO', 'BSECM']) {
            rawSend({ joinIndex: true, seg })
        }
        // Replay token subscriptions.
        subscriptions.forEach(payload => rawSend(payload))
    }

    ws.onmessage = (message) => {
        if (global.ws !== ws) return
        // binary frames only; ignore any text
        if (typeof message.data === 'string') return
        parseFrame(message.data)
    }

    ws.onerror = () => {
        if (global.ws !== ws) return
        console.log('LTP Socket Error')
    }

    ws.onclose = () => {
        if (global.ws === ws) global.ws = null
        console.log('LTP Socket DisConnected')
        if (!manualClose) scheduleReconnect()
    }
}

export const connect = (accessTokenArg) => {
    if (typeof WebSocket === 'undefined') return
    if (accessTokenArg) accessToken = accessTokenArg
    manualClose = false
    if (global.ws && (global.ws.readyState === WebSocket.OPEN || global.ws.readyState === WebSocket.CONNECTING)) return
    reconnectCount = 0
    clearReconnect()
    openSocket()
}

// Call on logout to stop the feed and prevent further reconnects.
export const disconnect = () => {
    manualClose = true
    clearReconnect()
    const ws = global.ws
    global.ws = null
    if (ws) { try { ws.close() } catch (_) {} }
}
